import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../utils/formatCurrency';
import { monthClosureApi } from '../services/api';

// Array de nombres de meses
const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const MonthClosure = ({ conversionRates }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  // Inicializar estados con valores por defecto
  const [selectedCurrency, setSelectedCurrency] = useState('GBP');
  const [selectedHistoryYear, setSelectedHistoryYear] = useState(new Date().getFullYear());
  const [monthlyHistory, setMonthlyHistory] = useState([]);
  const defaultClosureItems = [
    { id: 1, name: 'Caixabank', sign: '+', amount: 0, selected: true, currency: 'GBP' },
    { id: 2, name: 'Starling Martin', sign: '+', amount: 0, selected: true, currency: 'GBP' },
    { id: 3, name: 'Monzo Martin', sign: '+', amount: 0, selected: true, currency: 'GBP' },
    { id: 4, name: 'Barclays Martin', sign: '+', amount: 0, selected: true, currency: 'GBP' },
    { id: 5, name: 'Picpay Martin', sign: '+', amount: 0, selected: true, currency: 'GBP' },
    { id: 6, name: 'Revolut', sign: '+', amount: 0, selected: true, currency: 'GBP' },
    { id: 7, name: 'Starling Amanda', sign: '+', amount: 0, selected: true, currency: 'GBP' },
    { id: 8, name: 'Monzo Amanda', sign: '+', amount: 0, selected: true, currency: 'GBP' },
    { id: 9, name: 'Barclays Mona', sign: '+', amount: 0, selected: true, currency: 'GBP' },
    { id: 10, name: 'Dinheiro vivo', sign: '+', amount: 0, selected: true, currency: 'GBP' },
    { id: 11, name: 'Crypto', sign: '+', amount: 0, selected: false, currency: 'GBP' },
    { id: 12, name: 'Trading', sign: '+', amount: 0, selected: false, currency: 'GBP' },
    { id: 13, name: 'Monzo inversión', sign: '+', amount: 374.66, selected: false, currency: 'GBP' },
    { id: 14, name: 'Espacio Elemento inversión', sign: '+', amount: 17230.3, selected: false, currency: 'EUR' },
    { id: 15, name: 'Deposito del piso', sign: '+', amount: 1500, selected: false, currency: 'EUR' },
    { id: 16, name: 'Gastos do mes incluyendo vacaciones', sign: '-', amount: 3100.63, selected: false, currency: 'GBP', specialType: 'gastos' },
    { id: 17, name: 'Dinero economizado Martin actual', sign: '-', amount: 0, selected: false, currency: 'GBP', specialType: 'economizado' },
    { id: 18, name: 'Dinero economizado Amanda actual', sign: '-', amount: 0, selected: false, currency: 'GBP', specialType: 'economizado' },
    { id: 19, name: 'Dinero economizado Martin antiguo', sign: '-', amount: 1094.60, selected: false, currency: 'GBP' },
    { id: 20, name: 'Dinero economizado Amanda antiguo', sign: '-', amount: 247.73, selected: false, currency: 'GBP' },
    { id: 21, name: 'Dinero Jubilación', sign: '+', amount: 0, selected: false, currency: 'GBP', specialType: 'jubilacion' }
  ];

  const [closureItems, setClosureItems] = useState(defaultClosureItems);
  const [nextId, setNextId] = useState(22);

  // Cargar datos desde el servidor al iniciar
  useEffect(() => {
    loadDataFromServer();
  }, []);

  const loadDataFromServer = async () => {
    try {
      setLoading(true);
      const closure = await monthClosureApi.get();

      if (closure) {
        console.log('Datos de cierre cargados desde el servidor:', closure);
        setClosureItems(closure.items || defaultClosureItems);
        setNextId(closure.nextId || 22);
        setSelectedCurrency(closure.selectedCurrency || 'GBP');
        setSelectedHistoryYear(closure.selectedHistoryYear || new Date().getFullYear());
        setMonthlyHistory(closure.monthlyHistory || []);
      }
    } catch (error) {
      console.error('Error cargando datos de cierre mensual:', error);
      // Intentar cargar desde localStorage como fallback
      try {
        const saved = localStorage.getItem('saver-month-closure');
        if (saved) {
          const parsed = JSON.parse(saved);
          setClosureItems(parsed.items || defaultClosureItems);
          setNextId(parsed.nextId || 22);
          setSelectedCurrency(parsed.selectedCurrency || 'GBP');
          setSelectedHistoryYear(parsed.selectedHistoryYear || new Date().getFullYear());
          setMonthlyHistory(parsed.monthlyHistory || []);
        }
      } catch (e) {
        console.warn('Error cargando desde localStorage:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  // Guardar datos en el servidor cuando cambien (con debounce)
  useEffect(() => {
    if (!loading) {
      // Usar debounce para evitar demasiadas llamadas al servidor
      const timeoutId = setTimeout(() => {
        saveDataToServer();
      }, 1000); // Esperar 1 segundo después del último cambio

      return () => clearTimeout(timeoutId);
    }
  }, [closureItems, nextId, selectedCurrency, monthlyHistory, selectedHistoryYear, loading]);

  const saveDataToServer = async () => {
    const dataToSave = {
      items: closureItems,
      nextId: nextId,
      selectedCurrency: selectedCurrency,
      monthlyHistory: monthlyHistory,
      selectedHistoryYear: selectedHistoryYear
    };

    try {
      await monthClosureApi.update(dataToSave);
      console.log('Datos de cierre guardados en el servidor');
    } catch (error) {
      console.error('Error guardando datos de cierre mensual:', error);
    }
  };

  // Función para convertir moneda a GBP
  const convertToGBP = (amount, fromCurrency) => {
    if (!conversionRates || fromCurrency === 'GBP') return amount;
    
    switch (fromCurrency) {
      case 'EUR':
        return amount / (conversionRates.EUR || 1.165);
      case 'BRL':
        return amount / (conversionRates.BRL || 6.36);
      default:
        return amount;
    }
  };

  // Función para convertir de GBP a otra moneda
  const convertFromGBP = (amount, toCurrency) => {
    if (!conversionRates || toCurrency === 'GBP') return amount;
    
    switch (toCurrency) {
      case 'EUR':
        return amount * (conversionRates.EUR || 1.165);
      case 'BRL':
        return amount * (conversionRates.BRL || 6.36);
      default:
        return amount;
    }
  };

  // Función para obtener el símbolo de moneda
  const getCurrencySymbol = (currency) => {
    switch (currency) {
      case 'EUR': return '€';
      case 'BRL': return 'R$';
      default: return '£';
    }
  };

  // Calcular totales (siempre en GBP para los cálculos)
  const totalGeneral = closureItems
    .filter(item => !item.specialType || item.specialType !== 'jubilacion')
    .reduce((sum, item) => {
      const amountInGBP = convertToGBP(parseFloat(item.amount) || 0, item.currency);
      return sum + (item.sign === '+' ? amountInGBP : -amountInGBP);
    }, 0);

  const jubilacionAmount = closureItems
    .filter(item => item.specialType === 'jubilacion')
    .reduce((sum, item) => {
      const amountInGBP = convertToGBP(parseFloat(item.amount) || 0, item.currency);
      return sum + (item.sign === '+' ? amountInGBP : -amountInGBP);
    }, 0);

  const totalConJubilacion = totalGeneral + jubilacionAmount;

  // Calcular dinero para invertir con lógica especial
  const dineroParaInvertir = (() => {
    // Sumar todos los items marcados como invertir
    const sumaInvertir = closureItems
      .filter(item => item.selected)
      .reduce((sum, item) => {
        const amountInGBP = convertToGBP(parseFloat(item.amount) || 0, item.currency);
        return sum + (item.sign === '+' ? amountInGBP : -amountInGBP);
      }, 0);

    // Restar específicamente los items de gastos y economizado actual
    const gastosYEconomizado = closureItems
      .filter(item => item.specialType === 'gastos' || item.specialType === 'economizado')
      .reduce((sum, item) => {
        const amountInGBP = convertToGBP(parseFloat(item.amount) || 0, item.currency);
        return sum + amountInGBP; // Siempre positivo para restar
      }, 0);

    return sumaInvertir - gastosYEconomizado;
  })();

  // Funciones para manejar items
  const handleUpdateItem = (id, field, value) => {
    setClosureItems(prev => prev.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleAddItem = () => {
    const newItem = {
      id: nextId,
      name: 'Nuevo item',
      sign: '+',
      amount: 0,
      selected: false,
      currency: selectedCurrency
    };
    setClosureItems(prev => [...prev, newItem]);
    setNextId(prev => prev + 1);
  };

  const handleDeleteItem = (id) => {
    setClosureItems(prev => prev.filter(item => item.id !== id));
  };

  const handleClearAll = () => {
    if (window.confirm('Tem certeza de que deseja limpar todos os dados? Esta ação não pode ser desfeita.')) {
      setClosureItems([]);
      setNextId(1);
    }
  };

  // Función para cerrar el mes actual
  const handleCloseMonth = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth(); // 0-11
    const currentYear = currentDate.getFullYear();
    const monthKey = `${currentYear}-${currentMonth}`;
    
    console.log('Iniciando cierre del mes:', { currentMonth, currentYear, monthKey });
    console.log('Historial actual:', monthlyHistory);
    
    // Verificar si ya existe un cierre para este mes
    const existingIndex = monthlyHistory.findIndex(entry => entry.monthKey === monthKey);
    console.log('Índice existente:', existingIndex);
    
    const monthlyData = {
      monthKey,
      year: currentYear,
      month: currentMonth,
      date: currentDate.toISOString(),
      totalGeneral: totalGeneral,
      totalConJubilacion: totalConJubilacion,
      dineroParaInvertir: dineroParaInvertir,
      selectedCurrency: selectedCurrency,
      // Guardar snapshot de los items para referencia futura
      itemsSnapshot: JSON.parse(JSON.stringify(closureItems))
    };
    
    console.log('Datos a guardar:', monthlyData);
    
    if (existingIndex !== -1) {
      // Actualizar entrada existente
      const confirmed = window.confirm(
        `Já existe um fechamento para ${MONTH_NAMES[currentMonth]} ${currentYear}.\n\n` +
        `Deseja atualizá-lo com os valores atuais?`
      );
      
      if (confirmed) {
        const newHistory = [...monthlyHistory];
        newHistory[existingIndex] = monthlyData;
        console.log('Actualizando historial:', newHistory);
        setMonthlyHistory(newHistory);
        alert(`Fechamento de ${MONTH_NAMES[currentMonth]} ${currentYear} atualizado com sucesso`);
      }
    } else {
      // Agregar nueva entrada
      const confirmed = window.confirm(
        `Confirma o fechamento do mês de ${MONTH_NAMES[currentMonth]} ${currentYear}?\n\n` +
        `Total General: ${getCurrencySymbol(selectedCurrency)}${convertFromGBP(Math.abs(totalGeneral), selectedCurrency).toFixed(2)}\n` +
        `Total con Jubilación: ${getCurrencySymbol(selectedCurrency)}${convertFromGBP(Math.abs(totalConJubilacion), selectedCurrency).toFixed(2)}\n` +
        `Dinero para Invertir: ${getCurrencySymbol(selectedCurrency)}${convertFromGBP(Math.abs(dineroParaInvertir), selectedCurrency).toFixed(2)}`
      );
      
      if (confirmed) {
        console.log('Agregando nueva entrada al historial');
        setMonthlyHistory(prev => {
          const newHistory = [...prev, monthlyData];
          console.log('Nuevo historial:', newHistory);
          return newHistory;
        });
        alert(`Mês ${MONTH_NAMES[currentMonth]} ${currentYear} fechado com sucesso`);
      }
    }
  };

  // Función para borrar un cierre mensual específico
  const handleDeleteMonth = (monthKey, monthName, year) => {
    const confirmed = window.confirm(
      `Tem certeza de que deseja excluir o fechamento do mês de ${monthName} ${year}?\n\n` +
      `Esta ação não pode ser desfeita.`
    );
    
    if (confirmed) {
      console.log('Eliminando cierre del mes:', monthKey);
      setMonthlyHistory(prev => {
        const newHistory = prev.filter(entry => entry.monthKey !== monthKey);
        console.log('Historial después de eliminar:', newHistory);
        return newHistory;
      });
      alert(`Fechamento de ${monthName} ${year} excluído com sucesso`);
    }
  };

  // Función para calcular porcentaje de cambio
  const calculatePercentageChange = (current, previous) => {
    if (previous === 0) return current === 0 ? 0 : 100;
    return ((current - previous) / Math.abs(previous)) * 100;
  };

  // Obtener historial filtrado por año
  const getFilteredHistory = () => {
    return monthlyHistory
      .filter(entry => entry.year === selectedHistoryYear)
      .sort((a, b) => a.month - b.month);
  };

  // Obtener años disponibles en el historial
  const getAvailableHistoryYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [...new Set(monthlyHistory.map(entry => entry.year))];
    
    // Asegurar que el año actual esté siempre incluido
    if (!years.includes(currentYear)) {
      years.push(currentYear);
    }
    
    return years.sort((a, b) => b - a);
  };



  // Función para manejar el foco en inputs de número para seleccionar todo si es 0
  const handleAmountFocus = (e) => {
    const value = parseFloat(e.target.value);
    if (value === 0 || e.target.value === '0') {
      e.target.select();
    }
  };

  // Función mejorada para manejar cambios en amount
  const handleAmountChange = (id, value) => {
    // Permitir campos vacíos temporalmente
    const numValue = value === '' ? 0 : parseFloat(value);
    handleUpdateItem(id, 'amount', isNaN(numValue) ? 0 : numValue);
  };

  return (
    <div className="expense-card">
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent">
            Fechamento do Mês
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Gestão de receitas, gastos e dinheiro para investir
          </p>
        </div>
        <button 
          type="button"
          className="button-secondary text-xl"
        >
          {isExpanded ? '▼' : '▶'}
        </button>
      </div>
      
      {isExpanded && (
        <div className="mt-4 space-y-4">
          
          {/* Selector de moneda y controles principales */}
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mostrar em:
                </label>
                <select
                  value={selectedCurrency}
                  onChange={(e) => setSelectedCurrency(e.target.value)}
                  className="select-field"
                >
                  <option value="GBP">Libras (£)</option>
                  <option value="EUR">Euros (€)</option>
                  <option value="BRL">Reales (R$)</option>
                </select>
              </div>
              
              <button
                onClick={handleAddItem}
                className="button-primary flex items-center space-x-2"
              >
                  <span>Adicionar Item</span>
              </button>
            </div>
            
            <button
              onClick={handleClearAll}
              className="button-error flex items-center space-x-2"
            >
              <span>Limpar Tudo</span>
            </button>
          </div>

          {/* Lista de items */}
          <div className="space-y-2">
            {closureItems.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>Não há itens adicionados. Clique em "Adicionar Item" para começar.</p>
              </div>
            ) : (
              closureItems.map(item => (
                <div key={item.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  
                  {/* Nombre */}
                  <div className="flex-1">
                    <input
                      type="text"
                      value={item.name}
                      onChange={(e) => handleUpdateItem(item.id, 'name', e.target.value)}
                      className="w-full input-field"
                      placeholder="Nome do item"
                    />
                    {item.specialType && (
                      <div className="text-xs text-gray-500 mt-1">
                        {item.specialType === 'gastos' && 'Se resta del dinero para invertir'}
                        {item.specialType === 'economizado' && 'Se resta del dinero para invertir'}
                        {item.specialType === 'jubilacion' && 'Solo se suma al total con jubilación'}
                      </div>
                    )}
                  </div>
                  
                  {/* Signo +/- */}
                  <div className="w-16">
                    <select
                      value={item.sign}
                      onChange={(e) => handleUpdateItem(item.id, 'sign', e.target.value)}
                      className="w-full select-field"
                    >
                      <option value="+">+</option>
                      <option value="-">-</option>
                    </select>
                  </div>
                  
                  {/* Selector de moneda del item */}
                  <div className="w-20">
                    <select
                      value={item.currency || 'GBP'}
                      onChange={(e) => handleUpdateItem(item.id, 'currency', e.target.value)}
                      className="w-full select-field text-xs"
                    >
                      <option value="GBP">£</option>
                      <option value="EUR">€</option>
                      <option value="BRL">R$</option>
                    </select>
                  </div>
                  
                  {/* Importe */}
                  <div className="flex items-center space-x-2">
                    <span className="text-lg font-bold text-gray-700">
                      {getCurrencySymbol(item.currency || 'GBP')}
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      value={item.amount}
                      onChange={(e) => handleAmountChange(item.id, e.target.value)}
                      onFocus={handleAmountFocus}
                      className="w-32 input-field"
                      placeholder="0.00"
                    />
                  </div>
                  
                  {/* Selección para categoría separada */}
                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-gray-600">Investir:</label>
                    <input
                      type="checkbox"
                      checked={item.selected}
                      onChange={(e) => handleUpdateItem(item.id, 'selected', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  
                  {/* Botón eliminar */}
                  <button
                    onClick={() => handleDeleteItem(item.id)}
                    className="text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-1 rounded transition-colors"
                    title="Excluir item"
                  >
                    X
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Totales */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            
            {/* Total General */}
            <div className="expense-card-dark">
              <div className="text-center">
                <h3 className="text-lg font-bold text-white mb-2">
                  Total Geral
                </h3>
                <div className="w-16 h-1 mx-auto rounded-full bg-gradient-to-r from-blue-400 to-purple-400 mb-4"></div>
                <div className={`text-2xl font-bold mb-3 ${
                  totalGeneral >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {getCurrencySymbol(selectedCurrency)}{convertFromGBP(Math.abs(totalGeneral), selectedCurrency).toFixed(2)}
                </div>
                
                {/* Conversiones a otras monedas */}
                <div className="space-y-1 mb-3">
                  {selectedCurrency === 'GBP' && (
                    <>
                      <div className="text-xs text-gray-300">
                        (En EUR: €{convertFromGBP(Math.abs(totalGeneral), 'EUR').toFixed(2)})
                      </div>
                      <div className="text-xs text-gray-300">
                        (En R$: R${convertFromGBP(Math.abs(totalGeneral), 'BRL').toFixed(2)})
                      </div>
                    </>
                  )}
                  {selectedCurrency === 'EUR' && (
                    <>
                      <div className="text-xs text-gray-300">
                        (En GBP: £{Math.abs(totalGeneral).toFixed(2)})
                      </div>
                      <div className="text-xs text-gray-300">
                        (En R$: R${convertFromGBP(Math.abs(totalGeneral), 'BRL').toFixed(2)})
                      </div>
                    </>
                  )}
                  {selectedCurrency === 'BRL' && (
                    <>
                      <div className="text-xs text-gray-300">
                        (En GBP: £{Math.abs(totalGeneral).toFixed(2)})
                      </div>
                      <div className="text-xs text-gray-300">
                        (En EUR: €{convertFromGBP(Math.abs(totalGeneral), 'EUR').toFixed(2)})
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Total con Jubilación */}
            <div className="expense-card-dark">
              <div className="text-center">
                <h3 className="text-lg font-bold text-white mb-2">
                  Total com Aposentação
                </h3>
                <div className="w-16 h-1 mx-auto rounded-full bg-gradient-to-r from-purple-400 to-pink-400 mb-4"></div>
                <div className={`text-2xl font-bold mb-3 ${
                  totalConJubilacion >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {getCurrencySymbol(selectedCurrency)}{convertFromGBP(Math.abs(totalConJubilacion), selectedCurrency).toFixed(2)}
                </div>
                
                {/* Conversiones a otras monedas */}
                <div className="space-y-1 mb-3">
                  {selectedCurrency === 'GBP' && (
                    <>
                      <div className="text-xs text-gray-300">
                        (En EUR: €{convertFromGBP(Math.abs(totalConJubilacion), 'EUR').toFixed(2)})
                      </div>
                      <div className="text-xs text-gray-300">
                        (En R$: R${convertFromGBP(Math.abs(totalConJubilacion), 'BRL').toFixed(2)})
                      </div>
                    </>
                  )}
                  {selectedCurrency === 'EUR' && (
                    <>
                      <div className="text-xs text-gray-300">
                        (En GBP: £{Math.abs(totalConJubilacion).toFixed(2)})
                      </div>
                      <div className="text-xs text-gray-300">
                        (En R$: R${convertFromGBP(Math.abs(totalConJubilacion), 'BRL').toFixed(2)})
                      </div>
                    </>
                  )}
                  {selectedCurrency === 'BRL' && (
                    <>
                      <div className="text-xs text-gray-300">
                        (En GBP: £{Math.abs(totalConJubilacion).toFixed(2)})
                      </div>
                      <div className="text-xs text-gray-300">
                        (En EUR: €{convertFromGBP(Math.abs(totalConJubilacion), 'EUR').toFixed(2)})
                      </div>
                    </>
                  )}
                </div>
                
                <div className="text-sm text-gray-300">
                  Inclui aposentação: {formatCurrency(jubilacionAmount)}
                </div>
              </div>
            </div>

            {/* Dinero para Invertir */}
            <div className="expense-card-dark">
              <div className="text-center">
                <h3 className="text-lg font-bold text-white mb-2">
                  Dinheiro para Investir
                </h3>
                <div className="w-16 h-1 mx-auto rounded-full bg-gradient-to-r from-green-400 to-emerald-400 mb-4"></div>
                <div className={`text-2xl font-bold mb-3 ${
                  dineroParaInvertir >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {getCurrencySymbol(selectedCurrency)}{convertFromGBP(Math.abs(dineroParaInvertir), selectedCurrency).toFixed(2)}
                </div>
                
                {/* Conversiones a otras monedas */}
                <div className="space-y-1 mb-3">
                  {selectedCurrency === 'GBP' && (
                    <>
                      <div className="text-xs text-gray-300">
                        (En EUR: €{convertFromGBP(Math.abs(dineroParaInvertir), 'EUR').toFixed(2)})
                      </div>
                      <div className="text-xs text-gray-300">
                        (En R$: R${convertFromGBP(Math.abs(dineroParaInvertir), 'BRL').toFixed(2)})
                      </div>
                    </>
                  )}
                  {selectedCurrency === 'EUR' && (
                    <>
                      <div className="text-xs text-gray-300">
                        (En GBP: £{Math.abs(dineroParaInvertir).toFixed(2)})
                      </div>
                      <div className="text-xs text-gray-300">
                        (En R$: R${convertFromGBP(Math.abs(dineroParaInvertir), 'BRL').toFixed(2)})
                      </div>
                    </>
                  )}
                  {selectedCurrency === 'BRL' && (
                    <>
                      <div className="text-xs text-gray-300">
                        (En GBP: £{Math.abs(dineroParaInvertir).toFixed(2)})
                      </div>
                      <div className="text-xs text-gray-300">
                        (En EUR: €{convertFromGBP(Math.abs(dineroParaInvertir), 'EUR').toFixed(2)})
                      </div>
                    </>
                  )}
                </div>
                
                <div className="text-sm text-gray-300">
                  {closureItems.filter(item => item.selected).length} itens marcados
                </div>
              </div>
            </div>
          </div>

          {/* Botón para cerrar el mes actual */}
          <div className="flex justify-center mt-6">
            <button
              onClick={handleCloseMonth}
              className="button-primary flex items-center space-x-2"
            >
              <span>Fechar o Mês</span>
            </button>
          </div>

          {/* Historial de cierres mensuales */}
          <div className="mt-6">
            <h3 className="text-lg font-bold text-gray-800 mb-2 text-center">
              Histórico de Fechamentos Mensais
            </h3>
            <div className="w-16 h-1 mx-auto rounded-full bg-gradient-to-r from-blue-400 to-purple-400 mb-4"></div>
            
            {/* Selector de año */}
            <div className="flex justify-center mb-4">
              <select
                value={selectedHistoryYear}
                onChange={(e) => setSelectedHistoryYear(parseInt(e.target.value))}
                className="select-field"
              >
                {getAvailableHistoryYears().map(year => (
                  <option key={year} value={year}>
                    {year} {year === new Date().getFullYear() ? '(Actual)' : ''}
                  </option>
                ))}
              </select>
            </div>

            {getFilteredHistory().length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>Não há fechamentos registrados para {selectedHistoryYear}.</p>
                <p className="text-sm mt-2">Use o botão "Fechar o Mês" para registrar o primeiro fechamento.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {getFilteredHistory().map((entry, index) => {
                  const history = getFilteredHistory();
                  const previousEntry = index > 0 ? history[index - 1] : null;
                  
                  // Calcular porcentajes de cambio
                  const totalGeneralChange = previousEntry ? 
                    calculatePercentageChange(entry.totalGeneral, previousEntry.totalGeneral) : 0;
                  const totalJubilacionChange = previousEntry ?
                    calculatePercentageChange(entry.totalConJubilacion, previousEntry.totalConJubilacion) : 0;
                  const dineroInvertirChange = previousEntry ?
                    calculatePercentageChange(entry.dineroParaInvertir, previousEntry.dineroParaInvertir) : 0;
                  
                  return (
                    <div key={entry.monthKey} className="expense-card-dark">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-bold text-white text-lg">
                          {MONTH_NAMES[entry.month]} {entry.year}
                        </h4>
                        <div className="flex items-center space-x-3">
                          <span className="text-xs text-gray-400">
                            {new Date(entry.date).toLocaleDateString()}
                          </span>
                          <button
                            onClick={() => handleDeleteMonth(entry.monthKey, MONTH_NAMES[entry.month], entry.year)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-900 hover:bg-opacity-30 p-1 rounded transition-colors"
                            title={`Eliminar cierre de ${MONTH_NAMES[entry.month]} ${entry.year}`}
                          >
                            X
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Total General */}
                        <div className="text-center">
                          <div className="text-sm text-gray-300 mb-1">Total Geral</div>
                          <div className={`text-lg font-bold ${
                            entry.totalGeneral >= 0 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {getCurrencySymbol(entry.selectedCurrency)}{convertFromGBP(Math.abs(entry.totalGeneral), entry.selectedCurrency).toFixed(2)}
                          </div>
                          {previousEntry && (
                            <div className={`text-xs mt-1 ${
                              totalGeneralChange >= 0 ? 'text-green-300' : 'text-red-300'
                            }`}>
                              {totalGeneralChange >= 0 ? '↗️' : '↘️'} {totalGeneralChange.toFixed(1)}%
                            </div>
                          )}
                        </div>
                        
                        {/* Total con Jubilación */}
                        <div className="text-center">
                          <div className="text-sm text-gray-300 mb-1">Total com Aposentação</div>
                          <div className={`text-lg font-bold ${
                            entry.totalConJubilacion >= 0 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {getCurrencySymbol(entry.selectedCurrency)}{convertFromGBP(Math.abs(entry.totalConJubilacion), entry.selectedCurrency).toFixed(2)}
                          </div>
                          {previousEntry && (
                            <div className={`text-xs mt-1 ${
                              totalJubilacionChange >= 0 ? 'text-green-300' : 'text-red-300'
                            }`}>
                              {totalJubilacionChange >= 0 ? '↗️' : '↘️'} {totalJubilacionChange.toFixed(1)}%
                            </div>
                          )}
                        </div>
                        
                        {/* Dinero para Invertir */}
                        <div className="text-center">
                          <div className="text-sm text-gray-300 mb-1">Dinheiro para Investir</div>
                          <div className={`text-lg font-bold ${
                            entry.dineroParaInvertir >= 0 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {getCurrencySymbol(entry.selectedCurrency)}{convertFromGBP(Math.abs(entry.dineroParaInvertir), entry.selectedCurrency).toFixed(2)}
                          </div>
                          {previousEntry && (
                            <div className={`text-xs mt-1 ${
                              dineroInvertirChange >= 0 ? 'text-green-300' : 'text-red-300'
                            }`}>
                              {dineroInvertirChange >= 0 ? '↗️' : '↘️'} {dineroInvertirChange.toFixed(1)}%
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Resumen de comparación */}
                      {previousEntry && (
                        <div className="mt-3 pt-3 border-t border-gray-600">
                          <div className="text-xs text-gray-400 text-center">
                            Comparado com {MONTH_NAMES[previousEntry.month]} {previousEntry.year}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Información adicional */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-2">ℹ️ Informações do Módulo:</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Os títulos e números se mantêm fixos, a menos que você adicione novos itens</li>
              <li>• O <strong>Total Geral</strong> soma/subtrai todos os números (exceto aposentação)</li>
              <li>• O <strong>Total com Aposentação</strong> inclui também o dinheiro da aposentação</li>
              <li>• <strong>"Dinheiro para Investir"</strong> = Soma itens marcados - Gastos do mês - Dinheiro economizado atual</li>
              <li>• Todos os totais são exibidos na moeda selecionada, mas são calculados em GBP</li>
              <li>• Este módulo não tem relação com os outros módulos</li>
            </ul>
          </div>

        </div>
      )}
    </div>
  );
};

export default MonthClosure; 